import { type ReactNode, type ElementType, type HTMLAttributes } from "react";
import { cn } from "../../lib/cn";

import styles from "./Container.module.css";

/**
 * Other HTML attributes (aria-label, aria-labelledby, id, role, data-*) are
 * forwarded to the rendered element, so as="section" can be a named region.
 */
export interface ContainerProps
  extends Omit<HTMLAttributes<HTMLElement>, "children" | "className"> {
  /** Page content inside the width constraint. */
  children: ReactNode;
  /**
   * Max-width token. Defaults to `lg`.
   * @default "lg" 
   */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Center the container horizontally. Defaults to `true`. */
  center?: boolean;
  /** Wrapper class names. */
  className?: string;
  /**
   * Polymorphic element. Defaults to `div`. Typed as any React ElementType, so
   * the values list is every JSX intrinsic; only container elements (div, main,
   * section, article, aside, header, footer) make sense here.
   * @default "div" 
   */
  as?: ElementType;
}

const sizeClasses = {
  sm: "max-w-container-sm",   // 640px
  md: "max-w-container-md",   // 960px
  lg: "max-w-container-lg",   // 1200px
  xl: "max-w-container-xl",   // 1440px
  full: "max-w-full",
} as const;

/**
 * Container component.
 */
export function Container({
  children,
  size = "lg",
  center = true,
  className,
  as: Component = "div",
  ...rest
}: ContainerProps) {
  return (
    <Component
      {...rest}
      className={cn(
        styles.container,
        sizeClasses[size],
        center && styles.centered,
        className
      )}
    >
      {children}
    </Component>
  );
}
